/**
 * DEVOTIONALS (F4) — PURE progress, aggregation and streak logic.
 *
 * Sync already resolved isolation (streams parameterized by
 * `effective_grant`): a member's local SQLite contains only THEIR progress;
 * the moderator's contains the whole group's. These functions aggregate what is
 * available locally without re-filtering by permissions.
 */

import type { DayKey, StreakState } from "../gamification/types";
import { computeStreak, dayKeyUTC } from "../gamification/streaks";
import { deterministicUuid } from "../utils/deterministic-id";

/** Structural row of `devotional_item` (what aggregation uses). */
export interface DevotionalItemLike {
  id: string;
  plan_id: string | null;
  title: string | null;
  target_date: string | null;
  sort_order: number | null;
  deleted_at?: string | null;
}

/** Structural row of `devotional_progress`. */
export interface DevotionalProgressLike {
  id: string;
  item_id: string | null;
  plan_id: string | null;
  user_id: string | null;
  user_name: string | null;
  done: number | boolean | null;
  done_on: string | null;
  reflection: string | null;
}

/**
 * DETERMINISTIC progress ID derived from `<item>:<user>` (stable UUID).
 * Converges across devices (same pattern as F1's offline attendance): two
 * devices belonging to the same user marking the same item produce the SAME
 * row, not duplicates.
 */
export function devotionalProgressId(itemId: string, userId: string): string {
  return deterministicUuid(`devotional_progress:${itemId}:${userId}`);
}

export function isDone(p: Pick<DevotionalProgressLike, "done">): boolean {
  return p.done === true || p.done === 1;
}

/** Items in a plan in program order (sort_order, then title). */
export function orderDevotionalItems<T extends DevotionalItemLike>(items: readonly T[]): T[] {
  return items
    .filter((i) => !i.deleted_at)
    .toSorted(
      (a, b) =>
        (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.title ?? "").localeCompare(b.title ?? ""),
    );
}

export interface ItemProgressSummary {
  itemId: string;
  /** How many people marked it done. */
  doneCount: number;
  /** How many left a reflection. */
  reflectionCount: number;
}

export interface MemberProgressSummary {
  userId: string;
  userName: string | null;
  doneCount: number;
  reflectionCount: number;
  /** Ratio from 0..1 over the plan's items. */
  ratio: number;
}

export interface GroupProgressSummary {
  totalItems: number;
  perItem: ItemProgressSummary[];
  perMember: MemberProgressSummary[];
}

/**
 * Aggregation of progress for ONE plan (moderator view): by item and by
 * member. Counts progress only for live items in the plan.
 */
export function aggregateGroupProgress(
  items: readonly DevotionalItemLike[],
  progress: readonly DevotionalProgressLike[],
  planId: string,
): GroupProgressSummary {
  const planItems = orderDevotionalItems(items.filter((i) => i.plan_id === planId));
  const itemIds = new Set(planItems.map((i) => i.id));
  const rows = progress.filter((p) => p.item_id && itemIds.has(p.item_id));

  const perItem = planItems.map((item) => {
    const of = rows.filter((p) => p.item_id === item.id);
    return {
      itemId: item.id,
      doneCount: of.filter((p) => isDone(p)).length,
      reflectionCount: of.filter((p) => (p.reflection ?? "").trim().length > 0).length,
    };
  });

  const byMember = new Map<string, MemberProgressSummary>();
  for (const p of rows) {
    if (!p.user_id) {
      continue;
    }
    const entry = byMember.get(p.user_id) ?? {
      userId: p.user_id,
      userName: p.user_name,
      doneCount: 0,
      reflectionCount: 0,
      ratio: 0,
    };
    if (isDone(p)) {
      entry.doneCount += 1;
    }
    if ((p.reflection ?? "").trim().length > 0) {
      entry.reflectionCount += 1;
    }
    entry.userName ??= p.user_name;
    byMember.set(p.user_id, entry);
  }
  const perMember = [...byMember.values()]
    .map((m) => ({
      ...m,
      ratio: planItems.length > 0 ? m.doneCount / planItems.length : 0,
    }))
    .toSorted(
      (a, b) => b.doneCount - a.doneCount || (a.userName ?? "").localeCompare(b.userName ?? ""),
    );

  return { totalItems: planItems.length, perItem, perMember };
}

/**
 * Active devotional days for ONE account (for the streak): `done_on`
 * days from completed progress. Without `done_on` (legacy completion),
 * the day is ignored — the streak requires an explicit day.
 */
export function devotionalActiveDays(
  progress: readonly DevotionalProgressLike[],
  userId: string,
): DayKey[] {
  const days: DayKey[] = [];
  for (const p of progress) {
    if (p.user_id === userId && isDone(p) && p.done_on) {
      days.push(p.done_on);
    }
  }
  return [...new Set(days)].toSorted();
}

/** Account's devotional streak (gamification-core `computeStreak`). */
export function devotionalStreak(
  progress: readonly DevotionalProgressLike[],
  userId: string,
  today: DayKey = dayKeyUTC(Date.now()),
): StreakState {
  return computeStreak(devotionalActiveDays(progress, userId), today);
}
